import { useMutation } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { styleSheet } from './styles';

interface IExternalRedirectResponse {
	redirectUri: string;
}

/**
 * Handles the /oauth/external/:externalAuthId route.
 *
 * The externalAuthId comes from the URL path — the preceding /oauth/external route normalizes
 * any query-string-based entry into this clean path form before this component is reached.
 * Calls the backend to resolve a redirect URL, then navigates the browser there.
 * The route is private — unauthenticated users are sent to login first and returned here afterward.
 */
export const ExternalAuthRedirect: React.FC<RouteComponentProps<Record<string, string>>> = ({ match }) => {
	const userSession = useUserSession();
	const { externalAuthId } = match.params;

	const { data, isError, isIdle, mutate } = useMutation({
		mutationFn: () =>
			userSession.webServiceHelper.callAsync<IExternalRedirectResponse>('user/external/redirect', 'POST', {
				externalAuthId,
			}),
	});

	React.useEffect(() => {
		if (isIdle) {
			mutate();
		}
	}, [isIdle, mutate]);

	React.useEffect(() => {
		if (data?.redirectUri) {
			window.location.href = data.redirectUri;
		}
	}, [data]);

	const hasError = isError || (data != null && !data.redirectUri);

	if (hasError) {
		return (
			<div className={css(styleSheet.errorContainer)}>
				<h2>Login Error</h2>
				<p>There was an issue logging in. Please try again or contact support.</p>
				<a href='#/login' className={css(styleSheet.returnToLoginLink)}>
					Return to Login
				</a>
			</div>
		);
	}

	return (
		<div className={css(styleSheet.loadingContainer)}>
			<LoadingSpinner type='login' />
			<p className={css(styleSheet.text)}>Completing login...</p>
		</div>
	);
};
